import { PostsGridOptionsType } from "@/types/ModuleOptionsType";
import PostCard, {
  PostsGridElementType,
} from "../../components/PostCard/PostCard";
import { Section } from "../../components/Section/Section";
import styles from "./PostsGrid.module.scss";
import {
  Grid,
  GridContent,
  GridItem,
} from "@aleksundshantu/aunds-utils-react/primitives/grid";
import { fetchPosts } from "../../getData/fetchPosts";
import { useRouter } from "next/router";
import { Modul_Post_Grid } from "../../wp/types/acf-module-types";
import { useSublinesVisibility } from "../../hooks/useSublinesOverlinesVisibility";
import { ImageType } from "@/types/baseTypes";
import { checkModuleIsHidden } from "@/utils/checkModuleIsHidden";
import { Headline } from "@/components/Headline/Headline";
import { FadeInUp } from "../../components/Animation/ScrollAnimation";
import { mockPost } from "@/ui/mock-data";
import { CategoryOverviewPages } from "@/getData/getIndexedPostGridData";
import { GridPagination } from "@/components/Pagination/GridPagination";

export type PostsGridModuleType = Modul_Post_Grid;

interface PostsGridProps {
  data: PostsGridModuleType;
  postsGridOptions: PostsGridOptionsType;
  fallback_image?: ImageType | false;
  categories: CategoryOverviewPages[];
  isNobleElements?: boolean;
}
export type PostsGridDataType = {
  totalPosts: number;
  elements: PostsGridElementType[];
};
{
  /* jscpd:ignore-start */
}
const PostsGridModule = ({
  data,
  postsGridOptions,
  fallback_image,
  categories,
  isNobleElements,
}: PostsGridProps) => {
  const {
    background_color_class,
    headline_text_align_class,
    id,
    grid_columns = "two-cols",
    section_margin_class,
    section_padding_class,
    posts_per_page,
  } = data.layout;

  const {
    box: { show_border_radius, show_box_shadow, show_card_box },
    show_overline_subline,
    grid_options: { fallback_options },
    media_format,
  } = postsGridOptions ?? {};

  const { headline, headline_html_tag, overline, subline } = data.grid || {};
  const { showOverline, showSubline } = useSublinesVisibility(
    show_overline_subline,
  );

  const { locale, defaultLocale } = useRouter();

  //! check if module is hidden
  const isHidden = checkModuleIsHidden(data.settings);
  if (isHidden) {
    return null;
  }

  return (
    <Section
      id={id}
      className={`post-grid ${styles["posts-grid"]} ${background_color_class} ${section_padding_class} ${section_margin_class}`}
      disableColumns
    >
      <div className="columns is-multiline is-centered">
        {headline && (
          <div className="column is-12">
            <FadeInUp>
              <Headline
                headline_html_tag={headline_html_tag}
                headline={headline}
                extraClass={headline_text_align_class}
                overline={showOverline ? overline : ""}
                subline={showSubline ? subline : ""}
                disableAnimation
              />
            </FadeInUp>
          </div>
        )}

        <div className="column is-12">
          <FadeInUp>
            <Grid
              postsPerPage={posts_per_page}
              fetchItems={({ page, postsPerPage, offset, abortController }) => {
                return fetchPosts({
                  page,
                  postsPerPage,
                  offset,
                  module: data,
                  locale,
                  defaultLocale,
                  fallback_options,
                  fallback_image,
                  abortController,
                  isNobleElements,
                });
              }}
              renderLoading
            >
              {(gridPosts, loading) => {
                return (
                  <>
                    <GridContent gap="2rem">
                      {loading
                        ? Array.from({
                            length: Math.max(posts_per_page || 3, 3),
                          }).map((_, key) => (
                            <GridItem
                              key={key}
                              itemWidth={{
                                mobile: "100%",
                                tablet: "100%",
                                desktop: "100%",
                                widescreen: "100%",
                                fullhd: "33.3333333%",
                                ultrahd: "33.3333333%",
                              }}
                              itemHeight={"100%"}
                              className={styles["grid-item"]}
                            >
                              <PostCard
                                skeleton={true}
                                element={mockPost}
                                border_radius={show_border_radius}
                                box_shadow={show_box_shadow}
                                showBox={show_card_box}
                                grid_columns={grid_columns}
                                media_format={media_format}
                                allCategories={categories}
                                headlineLevel={
                                  headline_html_tag === "h3" ? 4 : 3
                                }
                                isNobleElements={isNobleElements}
                              />
                            </GridItem>
                          ))
                        : gridPosts.map((element, key) => (
                            <GridItem
                              key={key}
                              itemWidth={{
                                mobile: "100%",
                                tablet: "100%",
                                desktop: "100%",
                                widescreen: "100%",
                                fullhd: "33.3333333%",
                                ultrahd: "33.3333333%",
                              }}
                              itemHeight={"100%"}
                              className={styles["grid-item"]}
                            >
                              <PostCard
                                skeleton={false}
                                element={element as PostsGridElementType}
                                border_radius={show_border_radius}
                                box_shadow={show_box_shadow}
                                showBox={show_card_box}
                                grid_columns={grid_columns}
                                media_format={media_format}
                                allCategories={categories}
                                headlineLevel={
                                  headline_html_tag === "h3" ? 4 : 3
                                }
                                isNobleElements={isNobleElements}
                              />
                            </GridItem>
                          ))}
                    </GridContent>
                    <GridPagination />
                  </>
                );
              }}
            </Grid>
          </FadeInUp>
        </div>
      </div>
    </Section>
  );
};

{
  /* jscpd:ignore-end */
}

export default PostsGridModule;
